<script setup>
import { ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useFieldOrder } from 'dashboard/composables/useFieldOrder';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import AddAttribute from 'dashboard/routes/dashboard/settings/attributes/AddAttribute.vue';
import EditAttribute from 'dashboard/routes/dashboard/settings/attributes/EditAttribute.vue';

// Índice do modelo "Produto" em ATTRIBUTE_MODELS (settings/attributes/constants.js).
const PRODUCT_MODEL_TAB = 4;

const { t } = useI18n();
const store = useStore();
const { orderedFields, saveOrder } = useFieldOrder('product');
const attributesByModel = useMapGetter('attributes/getAttributesByModel');

const dialogRef = ref(null);
const deleteDialogRef = ref(null);
// O Dialog é um <dialog> nativo (camada de topo) e esconderia os modais de campo
// abertos por cima; por isso ele fecha enquanto o modal está aberto e reabre depois.
const modal = ref('');
const selectedAttribute = ref({});
const items = ref([]);

watch(
  orderedFields,
  fields => {
    items.value = fields;
  },
  { immediate: true }
);

const open = () => dialogRef.value?.open();

const labelFor = field =>
  field.type === 'custom'
    ? field.attribute.attributeDisplayName
    : t(field.labelKey);

const rawAttribute = field =>
  attributesByModel
    .value('product_attribute')
    .find(attribute => attribute.attribute_key === field.key);

const closeModal = () => {
  modal.value = '';
  selectedAttribute.value = {};
  dialogRef.value?.open();
};

const openModal = (type, field) => {
  selectedAttribute.value = field ? rawAttribute(field) || {} : {};
  dialogRef.value?.close();
  modal.value = type;
};

const onReorder = async reordered => {
  const previous = items.value;
  items.value = reordered;
  try {
    await saveOrder(reordered.map(field => field.id));
  } catch {
    items.value = previous;
    useAlert(t('PRODUCTS.FIELDS_DIALOG.REORDER_ERROR'));
  }
};

const askDelete = field => {
  selectedAttribute.value = rawAttribute(field) || {};
  deleteDialogRef.value?.open();
};

const confirmDelete = async () => {
  try {
    await store.dispatch('attributes/delete', selectedAttribute.value.id);
    useAlert(t('ATTRIBUTES_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch {
    useAlert(t('ATTRIBUTES_MGMT.DELETE.API.ERROR_MESSAGE'));
  } finally {
    selectedAttribute.value = {};
    deleteDialogRef.value?.close();
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="t('PRODUCTS.FIELDS_DIALOG.TITLE')"
    :show-confirm-button="false"
  >
    <div class="flex flex-col gap-4">
      <div class="flex justify-end">
        <Button
          type="button"
          sm
          solid
          blue
          icon="i-lucide-plus"
          :label="t('PRODUCTS.FIELDS_DIALOG.ADD')"
          @click="openModal('add')"
        />
      </div>

      <div
        class="flex items-center gap-3 px-3 py-3 border border-dashed rounded-lg border-n-strong"
      >
        <span class="size-4 shrink-0" />
        <span class="text-sm text-n-slate-11 w-40 shrink-0">
          {{ t('PRODUCTS.FIELDS.NAME') }}
        </span>
        <span class="text-sm text-n-slate-10">
          {{ t('PRODUCTS.FIELDS_DIALOG.EMPTY_VALUE') }}
        </span>
      </div>

      <Draggable
        :model-value="items"
        item-key="id"
        tag="div"
        handle=".drag-handle"
        class="flex flex-col gap-3"
        @update:model-value="onReorder"
      >
        <template #item="{ element: field }">
          <div
            class="flex items-center gap-3 px-3 py-3 border border-dashed rounded-lg border-n-strong"
          >
            <span
              class="cursor-grab drag-handle i-lucide-grip-vertical size-4 shrink-0 text-n-slate-10"
            />
            <span class="text-sm truncate text-n-slate-11 w-40 shrink-0">
              {{ labelFor(field) }}
            </span>
            <span class="flex-1 text-sm text-n-slate-10">
              {{
                field.type === 'standard'
                  ? t('PRODUCTS.FIELDS_DIALOG.STANDARD_HINT')
                  : t('PRODUCTS.FIELDS_DIALOG.EMPTY_VALUE')
              }}
            </span>
            <div v-if="field.type === 'custom'" class="flex items-center gap-1">
              <Button
                type="button"
                sm
                ghost
                slate
                icon="i-lucide-pencil"
                :title="t('PRODUCTS.FIELDS_DIALOG.EDIT')"
                @click="openModal('edit', field)"
              />
              <Button
                type="button"
                sm
                ghost
                ruby
                icon="i-lucide-trash"
                :title="t('PRODUCTS.FIELDS_DIALOG.DELETE')"
                @click="askDelete(field)"
              />
            </div>
          </div>
        </template>
      </Draggable>
    </div>
  </Dialog>

  <Dialog
    ref="deleteDialogRef"
    type="alert"
    :title="
      t('ATTRIBUTES_MGMT.DELETE.CONFIRM.TITLE', {
        attributeName: selectedAttribute.attribute_display_name,
      })
    "
    :description="t('ATTRIBUTES_MGMT.DELETE.CONFIRM.MESSAGE')"
    :confirm-button-label="t('PRODUCTS.DELETE.CONFIRM')"
    @confirm="confirmDelete"
  />

  <AddAttribute
    v-if="modal === 'add'"
    show
    :on-close="closeModal"
    :selected-attribute-model-tab="PRODUCT_MODEL_TAB"
  />
  <woot-modal :show="modal === 'edit'" :on-close="closeModal">
    <EditAttribute
      v-if="modal === 'edit'"
      :selected-attribute="selectedAttribute"
      @on-close="closeModal"
    />
  </woot-modal>
</template>
